import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../../ui/icons';
import { OnboardingStep, useOnboarding } from '../../../contexts/OnboardingContext';
import { useTheme } from '@mui/material/styles';

type TooltipPositionType = 'top' | 'bottom';

export interface OnboardingTooltipProps {
  step: OnboardingStep;
  targetId?: string;
  title: string;
  description: string;
  position?: TooltipPositionType;
  showNext?: boolean;
  showExit?: boolean;
  showAddNow?: boolean;
  onNext?: () => void;
  onAddNow?: () => void;
}

const STEP_ORDER = [
  OnboardingStep.OVERVIEW_CHART,
  OnboardingStep.SPENDING_DISTRIBUTION,
  OnboardingStep.SPENDING_TRENDS,
  OnboardingStep.ASSET_OVERVIEW,
  OnboardingStep.ASSET_FLUCTUATION,
  OnboardingStep.ADD_TRANSACTION,
];

const TOTAL_STEPS = STEP_ORDER.length;
const HIGHLIGHT_PADDING = 0;
const TOOLTIP_GAP = 16;
const TOOLTIP_MARGIN = 16;

const OnboardingTooltip = ({
  step,
  targetId,
  title,
  description,
  position = 'bottom',
  showNext = true,
  showExit = false,
  showAddNow = false,
  onNext,
  onAddNow,
}: OnboardingTooltipProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['onboarding']);
  const { currentStep, nextStep, skipOnboarding } = useOnboarding();
  const [positionStyles, setPositionStyles] = useState<Record<string, number | string>>({});

  const stepNumber = STEP_ORDER.indexOf(step) + 1;

  const calculatePosition = useCallback(() => {
    const defaultPosition = { top: 200, left: TOOLTIP_MARGIN, right: TOOLTIP_MARGIN };

    if (!targetId) return defaultPosition;

    const element = document.querySelector(`[data-onboarding-id="${targetId}"]`);
    if (!element) return defaultPosition;

    const rect = element.getBoundingClientRect();

    // Extra gap for ADD_TRANSACTION step to account for the floating button
    const extraGap = step === OnboardingStep.ADD_TRANSACTION ? 30 : 0;

    if (position === 'top') {
      return {
        bottom: window.innerHeight - rect.top + HIGHLIGHT_PADDING + TOOLTIP_GAP + extraGap,
        left: TOOLTIP_MARGIN,
        right: TOOLTIP_MARGIN,
      };
    }

    return {
      top: rect.bottom + HIGHLIGHT_PADDING + TOOLTIP_GAP,
      left: TOOLTIP_MARGIN,
      right: TOOLTIP_MARGIN,
    };
  }, [targetId, position, step]);

  useEffect(() => {
    const updatePosition = () => setPositionStyles(calculatePosition());

    const timeoutId = setTimeout(updatePosition, 150);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      observer.disconnect();
    };
  }, [calculatePosition]);

  if (currentStep !== step) return null;

  const handleNext = () => (onNext ? onNext() : nextStep());

  const caretStyles = {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    ...(position === 'bottom'
      ? { top: -10, borderBottom: `10px solid ${theme.palette.navy[900]}` }
      : { bottom: -10, borderTop: `10px solid ${theme.palette.navy[900]}` }),
  };

  return (
    <Box sx={{ position: 'fixed', zIndex: theme.zIndex.modal + 10, ...positionStyles }}>
      <Paper
        elevation={8}
        sx={{
          p: 2.5,
          borderRadius: 3,
          bgcolor: 'navy.900',
          color: 'common.white',
          position: 'relative',
          '&::before': caretStyles,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="body2">
            {stepNumber}/{TOTAL_STEPS}
          </Typography>
          <IconButton
            onClick={skipOnboarding}
            size="small"
            sx={{ color: 'common.white', p: 0.5, mr: -1, mt: -1 }}
          >
            <CloseIcon style={{ fontSize: 22 }} />
          </IconButton>
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} color="common.white" mb={1}>
          {title}
        </Typography>

        <Typography lineHeight={1.6} mb={2}>
          {description}
        </Typography>

        {showNext && (
          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            {showAddNow && (
              <Button
                variant="outlined"
                size="small"
                onClick={onAddNow}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 6,
                  borderColor: 'grey.200',
                  color: 'common.white',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                {t('add_now')}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleNext}
              sx={{
                px: 6,
                bgcolor: 'common.white',
                color: 'text.primary',
              }}
            >
              {showExit ? t('finish') : t('next')}
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default OnboardingTooltip;
